/*----------------------------------
- LANDING STRUCTURED DATA
----------------------------------*/

import { PRICING_PLAN_SUMMARY_SENTENCE } from '@/client/catalogs/landing/pricing/seo';
import type { Thing } from 'schema-dts';

import {
    isPricingPlanCheckoutEnabled,
    PRICING_PLAN_CATALOG,
    PUBLIC_PLAN_FACTS,
    PUBLISHED_PLAN_ORDER,
    PUBLIC_PRICING_PLAN_ORDER,
} from '@uniquedomains/components/pricing/catalogs/pricingPlans';
import type { PublicPricingPlanCatalog } from '@uniquedomains/components/pricing/types';
import type { WebsiteDomainsLandingSeoFaq, WebsiteDomainsSearchRow } from '@/common/types/domains/websiteLanding';
import type { GeoContext } from '@uniquedomains/components/geo/pageGeo';
import type {
    PublishedNewsEdition,
    PublishedNewsEditionCard,
} from '@uniquedomains/components/contracts/news/public';
import { DOMAINING_NEWS_SECTION_LABELS } from '@uniquedomains/components/news/sections';
import { appErrorReporter } from '@/common/libs/errorReporting';
import { HOME_SCREENER_BLUEPRINT } from '@/common/catalogs/landing/homeScreenerBlueprint';
import type { AgendaEntry } from '@uniquedomains/components/contracts/agenda/public';
import { PUBLIC_AUCTIONS_ROW_LIMIT } from '@uniquedomains/components/contracts/auctions/public';
import { formatTldCount } from '@uniquedomains/components/contracts/tlds/format';
import type { PublicTldApplication, PublicTldInsight } from '@uniquedomains/components/contracts/tlds/public';
import { TLD_INSIGHT_SAMPLE_LIMIT, formatTldLabel } from '@uniquedomains/core/domains/tlds';

/*----------------------------------
- TYPES
----------------------------------*/

export type WebsiteJsonLdValue =
    | string
    | number
    | boolean
    | null
    | WebsiteJsonLdValue[]
    | WebsiteJsonLdRecord;
export type WebsiteJsonLdRecord = { [key: string]: WebsiteJsonLdValue };
export type WebsiteJsonLdObject = WebsiteJsonLdRecord & { '@type': string | string[] };
export type WebsiteJsonLdNode = Thing;

type BreadcrumbEntry = {
    name: string;
    url: string;
};

type DomainsStructuredDataKind = 'domains_index' | 'domains_search' | 'sector';

type DomainsStructuredDataContext = {
    canonicalUrl: string;
    currentPath: string;
    description: string;
    faqs: WebsiteDomainsLandingSeoFaq[];
    geoContext?: GeoContext;
    previewRows: WebsiteDomainsSearchRow[];
    title: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** How an offer describes its charge. A free plan is not a subscription. */
const OFFER_CATEGORY_BY_BILLING: Readonly<Record<'free' | 'one_time' | 'monthly', string>> = {
    free: 'free',
    one_time: 'one-time payment',
    monthly: 'subscription',
};

const PRODUCT_DESCRIPTION =
    'Unique Domains is a one-word domain intelligence platform that helps users find, evaluate, monitor, and act on one-word domain opportunities.';
const SOFTWARE_DESCRIPTION =
    'A one-word domain intelligence platform for finding, evaluating, monitoring, and acting on one-word domain opportunities.';
/*----------------------------------
- HELPERS
----------------------------------*/

const toUrl = (value: string, fallbackOrigin = 'https://unique.domains'): URL => {
    try {
        return new URL(value, fallbackOrigin);
    } catch (error) {
        appErrorReporter.app.handleError(error);
        console.warn('[structured-data] Could not parse URL; using root fallback.', error);
        return new URL('/', fallbackOrigin);
    }
};

const originFromUrl = (url: string): string => toUrl(url).origin;

const decodeSegment = (value: string | undefined): string => {
    if (!value) return '';

    try {
        return decodeURIComponent(value).trim();
    } catch (error) {
        appErrorReporter.app.handleError(error);
        console.warn('[structured-data] Could not decode path segment.', error);
        return value.trim();
    }
};

const sectorLabelFromSlug = (slug: string): string => {
    const label = decodeSegment(slug).replace(/[-_]+/g, ' ').trim();
    if (label.toLowerCase() === 'ai') return 'AI';
    if (label.toLowerCase() === 'saas') return 'SaaS';
    return label || 'this sector';
};

const resolveDomainsStructuredDataKind = (canonicalUrl: string, currentPath: string): DomainsStructuredDataKind => {
    const canonical = toUrl(canonicalUrl);
    const current = toUrl(currentPath || canonical.pathname, canonical.origin);
    const normalizedPath = current.pathname.replace(/\/+$/, '') || '/domains';
    const sectorMatch = normalizedPath.match(/^\/domains\/sector\/([^/]+)$/);
    const slugMatch = normalizedPath.match(/^\/domains\/([^/]+)$/);

    if (sectorMatch?.[1]) return 'sector';
    if (slugMatch?.[1]) return 'domains_search';

    return canonical.search || current.search ? 'domains_search' : 'domains_index';
};

/**
 * Every schema node in this file passes through here on its way to `page.jsonld`.
 *
 * @fix  docs/fixes/2026-09-07-news-summary-rejected-a-comparison-operator.md
 * @rule Values reaching a node are NOT escaped: Proteum renders `page.jsonld` with a bare
 *       `JSON.stringify` inside a `<script type="application/ld+json">` tag, and the
 *       escape would have to happen after serialization, so it cannot be added here. Any
 *       model-written or third-party string put on a node must already be refused an
 *       element-closing sequence upstream, as the News validator does.
 */
const jsonLdNode = (node: WebsiteJsonLdObject): WebsiteJsonLdNode => node as WebsiteJsonLdNode;

const breadcrumbSchema = (canonicalUrl: string, entries: BreadcrumbEntry[]): WebsiteJsonLdNode => jsonLdNode({
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: entries.map((entry, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: entry.name,
        item: entry.url,
    })),
});

const itemListSchema = ({
    canonicalUrl,
    name,
    previewRows,
}: {
    canonicalUrl: string;
    name: string;
    previewRows: WebsiteDomainsSearchRow[];
}): WebsiteJsonLdNode => {
    const rows = previewRows.slice(0, 10);

    return jsonLdNode({
        '@type': 'ItemList',
        '@id': `${canonicalUrl}#itemlist`,
        isPartOf: { '@id': `${canonicalUrl}#webpage` },
        name,
        numberOfItems: rows.length,
        itemListElement: rows.map((row, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: row.domain,
        })),
    });
};

/** Six questions by default (the `/domains` FAQ); a page whose whole FAQ is markup passes its own count. */
const FAQ_SCHEMA_DEFAULT_LIMIT = 6;

const faqSchema = (canonicalUrl: string, faqs: WebsiteDomainsLandingSeoFaq[], limit = FAQ_SCHEMA_DEFAULT_LIMIT): WebsiteJsonLdNode | null => {
    if (!faqs.length) return null;

    return jsonLdNode({
        '@type': 'FAQPage',
        '@id': `${canonicalUrl}#faq`,
        isPartOf: { '@id': `${canonicalUrl}#webpage` },
        mainEntity: faqs.slice(0, limit).map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
            },
        })),
    });
};

/**
 * Page FAQ catalogs speak `question`/`answer`; `FAQPage` markup speaks `q`/`a`.
 *
 * Every page that emits an FAQ needs this translation, so it lives next to the
 * builder that consumes it rather than being re-typed at each call site.
 */
export const toStructuredDataFaqs = (
    items: ReadonlyArray<{ question: string; answer: string }>,
): WebsiteDomainsLandingSeoFaq[] => items.map((item) => ({ q: item.question, a: item.answer }));

/* Monthly only. `'ANN'` was the second member until 2026-09-03, for an annual offer
   published whenever a plan's yearly rate undercut its monthly one, and nothing has
   been sold that way since the single-plan release. */
const unitPriceSpecification = (
    price: number,
    billingDuration: number,
    unitCode: 'MON',
): WebsiteJsonLdRecord => ({
    '@type': 'UnitPriceSpecification',
    price: String(price),
    priceCurrency: 'USD',
    billingDuration,
    billingIncrement: 1,
    unitCode,
});

const compactNodes = (nodes: Array<WebsiteJsonLdNode | null>): WebsiteJsonLdNode[] =>
    nodes.filter((node): node is WebsiteJsonLdNode => Boolean(node));

/*----------------------------------
- GLOBAL NODES
----------------------------------*/

/**
 * Publisher identity is emitted once per page, by the framework.
 *
 * Proteum's page response appends `Organization` and `WebSite` to every HTML
 * page's `@graph`, built from the app identity, so `#organization` and
 * `#website` always resolve. It adds its generic `WebPage` only on pages that
 * push no `WebPage` or subtype themselves (Proteum 2.5.21; before that every page carried two
 * `WebPage` nodes for one URL). The builders here reference the identity ids
 * and must never emit those nodes themselves: a second node with the same `@id`
 * and a different `name`, `description` or `logo` is merged by consumers into
 * one contradictory entity, which is worse than either version alone.
 */
export const softwareApplicationSchema = (origin: string, offers?: WebsiteJsonLdObject[]): WebsiteJsonLdNode => jsonLdNode({
    '@type': 'SoftwareApplication',
    '@id': `${origin}/#software`,
    name: 'Unique Domains',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: `${origin}/`,
    description: SOFTWARE_DESCRIPTION,
    publisher: { '@id': `${origin}/#organization` },
    ...(offers?.length ? { offers } : {}),
});

const screenerFilmId = (canonicalUrl: string): string => `${canonicalUrl}#${HOME_SCREENER_BLUEPRINT.id}`;

/**
 * The screener film as a `VideoObject`, owned by the page that shows it.
 *
 * Home showed the film until 2026-09-27; it now renders the real row preview, and the film moved
 * under the `/product/search` hero, so the node moved with it. A page must only declare a video it
 * actually renders.
 */
const screenerFilmSchema = (canonicalUrl: string, origin: string): WebsiteJsonLdNode => jsonLdNode({
    '@type': 'VideoObject',
    '@id': screenerFilmId(canonicalUrl),
    name: HOME_SCREENER_BLUEPRINT.name,
    description: HOME_SCREENER_BLUEPRINT.description,
    // One VideoObject per page, describing the landscape master. The
    // portrait cut is a delivery variant of the same film, not a second
    // video, so declaring it here would announce a duplicate.
    thumbnailUrl: new URL(
        HOME_SCREENER_BLUEPRINT.variants.desktop.posterWebpPath,
        origin,
    ).toString(),
    contentUrl: new URL(HOME_SCREENER_BLUEPRINT.variants.desktop.mp4Path, origin).toString(),
    encodingFormat: 'video/mp4',
    uploadDate: HOME_SCREENER_BLUEPRINT.uploadDate,
    duration: HOME_SCREENER_BLUEPRINT.duration,
    width: HOME_SCREENER_BLUEPRINT.variants.desktop.videoWidth,
    height: HOME_SCREENER_BLUEPRINT.variants.desktop.videoHeight,
    publisher: { '@id': `${origin}/#organization` },
    about: { '@id': `${origin}/#software` },
    isPartOf: { '@id': `${canonicalUrl}#webpage` },
});

/*----------------------------------
- PAGE BUILDERS
----------------------------------*/

export const buildHomeStructuredData = (
    canonicalUrl: string,
    geoContext?: GeoContext,
    faqs: WebsiteDomainsLandingSeoFaq[] = [],
): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);
    const name = geoContext?.title || 'Unique Domains - one-word domain intelligence';
    const description =
        geoContext?.shortAnswer ||
        'Find, evaluate, monitor, and act on one-word domain opportunities through the Screener, Radars, and Watchlists.';

    // No rows here: the Home preview is a bounded demonstration (ADR-0028), not a dataset the
    // page's graph should publish.
    return compactNodes([
        softwareApplicationSchema(origin),
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name,
            description,
            isPartOf: { '@id': `${origin}/#website` },
            about: { '@id': `${origin}/#software` },
            mainEntity: { '@id': `${origin}/#software` },
        }),
        faqSchema(canonicalUrl, faqs),
    ]);
};

/**
 * One `Offer` per public plan, with the shape of the charge stated.
 *
 * A bare `price` cannot distinguish a one-time charge from a monthly one, so
 * recurring plans carry a `UnitPriceSpecification` with a billing duration, and a
 * yearly variant alongside it when yearly is genuinely a different price.
 *
 * Only published plans are emitted. A retired plan is not `SoldOut`, which reads
 * as temporarily unavailable and invites a search engine to keep surfacing its
 * price; it is simply not an offer any more.
 */
export const buildPricingOffers = (
    origin: string,
    plans: Readonly<PublicPricingPlanCatalog>,
): WebsiteJsonLdObject[] => {
    /* The fetched catalog as is: the free card the catalog builder used to merge in went with
       Explorer (ADR-0083), and the published order is the one sellable plan, so the page emits
       exactly one Offer. */
    return PUBLISHED_PLAN_ORDER.reduce<WebsiteJsonLdObject[]>((offers, planId) => {
        const plan = plans[planId];
        if (!plan) return offers;

        const facts = PUBLIC_PLAN_FACTS[planId];
        const isPurchasable = isPricingPlanCheckoutEnabled(plan.id);
        const price = plan.price;
        const hasPrice = typeof price === 'number' && price >= 0;

        offers.push({
            '@type': 'Offer',
            '@id': `${origin}/pricing#${plan.id}`,
            name: PRICING_PLAN_CATALOG[planId].marketingTagShort,
            description: plan.description,
            category: OFFER_CATEGORY_BY_BILLING[facts?.billing || 'monthly'],
            url: `${origin}/pricing`,
            availability: isPurchasable ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
            // An unavailable plan carries no price: quoting one asserts a purchase
            // the site would refuse.
            ...(isPurchasable && hasPrice ? { price: String(price), priceCurrency: 'USD' } : {}),
            ...(isPurchasable && facts?.billing === 'monthly'
                ? {
                    /* One monthly offer, because there is one billing period. The
                       annual offer beside it was published whenever a plan's yearly
                       rate undercut its monthly one, and no plan has been sold that
                       way since the single-plan release: it advertised a commitment
                       nobody could buy. */
                    priceSpecification: [unitPriceSpecification(facts.price, 1, 'MON')],
                }
                : {}),
        });

        return offers;
    }, []);
};

export const buildPricingStructuredData = (
    canonicalUrl: string,
    plans: Readonly<PublicPricingPlanCatalog>,
    geoContext?: GeoContext,
    faqs: WebsiteDomainsLandingSeoFaq[] = [],
): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);
    const name = geoContext?.title || 'Unique Domains pricing';
    // The pricing page's own summary sentence, built from the facts (ADR-0083, R17).
    const description = PRICING_PLAN_SUMMARY_SENTENCE;

    return compactNodes([
        softwareApplicationSchema(origin, buildPricingOffers(origin, plans)),
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name,
            description,
            isPartOf: { '@id': `${origin}/#website` },
            about: { '@id': `${origin}/#software` },
        }),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'Pricing', url: canonicalUrl },
        ]),
        faqSchema(canonicalUrl, faqs),
    ]);
};

export const buildProductFeatureStructuredData = ({
    canonicalUrl,
    description,
    faqs,
    featureLabel,
    geoContext,
    includeScreenerFilm = false,
    title,
}: {
    canonicalUrl: string;
    description: string;
    faqs: WebsiteDomainsLandingSeoFaq[];
    featureLabel: string;
    geoContext?: GeoContext;
    /** Set by the one feature page that renders the screener film under its hero (Search). */
    includeScreenerFilm?: boolean;
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);

    return compactNodes([
        softwareApplicationSchema(origin),
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: geoContext?.title || title,
            description: geoContext?.shortAnswer || description,
            isPartOf: { '@id': `${origin}/#website` },
            about: { '@id': `${origin}/#software` },
            ...(includeScreenerFilm ? { video: { '@id': screenerFilmId(canonicalUrl) } } : {}),
        }),
        includeScreenerFilm ? screenerFilmSchema(canonicalUrl, origin) : null,
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: featureLabel, url: canonicalUrl },
        ]),
        faqSchema(canonicalUrl, faqs),
    ]);
};

/**
 * `/glossary`: one `DefinedTermSet` holding every term, each anchored to its own entry.
 *
 * The shape the data page's methods published until that page was removed on 2026-10-04, and the
 * reason this page exists: a definition an answer engine can quote, attributed to a URL it can link
 * back to.
 */
export const buildGlossaryStructuredData = ({
    canonicalUrl,
    description,
    terms,
    title,
}: {
    canonicalUrl: string;
    description: string;
    terms: ReadonlyArray<{ id: string; term: string; definition: string }>;
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);

    return compactNodes([
        softwareApplicationSchema(origin),
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            isPartOf: { '@id': `${origin}/#website` },
            about: { '@id': `${origin}/#software` },
        }),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'Glossary', url: canonicalUrl },
        ]),
        jsonLdNode({
            '@type': 'DefinedTermSet',
            '@id': `${canonicalUrl}#terms`,
            name: title,
            description,
            hasDefinedTerm: terms.map((entry) => ({
                '@type': 'DefinedTerm',
                name: entry.term,
                url: `${canonicalUrl}#${entry.id}`,
                description: entry.definition,
                inDefinedTermSet: { '@id': `${canonicalUrl}#terms` },
            })),
        }),
    ]);
};

export const buildCompareStructuredData = ({
    canonicalUrl,
    competitorName,
    description,
    faqs,
    geoContext,
    title,
}: {
    canonicalUrl: string;
    competitorName: string;
    description: string;
    faqs: WebsiteDomainsLandingSeoFaq[];
    geoContext?: GeoContext;
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);

    return compactNodes([
        softwareApplicationSchema(origin),
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: geoContext?.title || title,
            description: geoContext?.shortAnswer || description,
            isPartOf: { '@id': `${origin}/#website` },
            about: { '@id': `${origin}/#software` },
        }),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'Compare', url: `${origin}/compare` },
            { name: `Unique Domains vs ${competitorName}`, url: canonicalUrl },
        ]),
        faqSchema(canonicalUrl, faqs),
    ]);
};

export const buildCompareHubStructuredData = (
    canonicalUrl: string,
    pages: Array<{ name: string; url: string }>,
    geoContext?: GeoContext,
): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);

    return [
        softwareApplicationSchema(origin),
        jsonLdNode({
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: geoContext?.title || 'Unique Domains comparisons',
            description:
                geoContext?.shortAnswer ||
                'Honest comparisons between Unique Domains and other domain databases, marketplaces, appraisal tools, and screeners.',
            isPartOf: { '@id': `${origin}/#website` },
            mainEntity: { '@id': `${canonicalUrl}#itemlist` },
        }),
        jsonLdNode({
            '@type': 'ItemList',
            '@id': `${canonicalUrl}#itemlist`,
            isPartOf: { '@id': `${canonicalUrl}#webpage` },
            name: 'Unique Domains comparison pages',
            numberOfItems: pages.length,
            itemListElement: pages.map((page, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: page.name,
                url: page.url,
            })),
        }),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'Compare', url: canonicalUrl },
        ]),
    ];
};

/**
 * The API documentation page.
 *
 * It is a `TechArticle` rather than a product page: the endpoints it documents
 * are not released on any plan, so the page describes a contract rather than
 * offering one, and the `SoftwareApplication` node stays out of it.
 */
export const buildApiStructuredData = ({
    canonicalUrl,
    description,
    faqs,
    title,
}: {
    canonicalUrl: string;
    description: string;
    faqs: WebsiteDomainsLandingSeoFaq[];
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);

    return compactNodes([
        jsonLdNode({
            '@type': 'TechArticle',
            '@id': `${canonicalUrl}#article`,
            headline: title,
            description,
            inLanguage: 'en',
            author: { '@id': `${origin}/#organization` },
            publisher: { '@id': `${origin}/#organization` },
            mainEntityOfPage: { '@id': `${canonicalUrl}#webpage` },
        }),
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: { '@id': `${origin}/#website` },
            mainEntity: { '@id': `${canonicalUrl}#article` },
        }),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'API', url: canonicalUrl },
        ]),
        faqSchema(canonicalUrl, faqs),
    ]);
};

/**
 * The team page.
 *
 * `AboutPage` with the organization as its subject. Individual team members are
 * deliberately not emitted as `Person` nodes: the page names real people, and
 * publishing them as structured data would syndicate those identities well
 * beyond the page they agreed to appear on.
 */
export const buildTeamStructuredData = ({
    canonicalUrl,
    description,
    title,
}: {
    canonicalUrl: string;
    description: string;
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);

    return [
        jsonLdNode({
            '@type': 'AboutPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: { '@id': `${origin}/#website` },
            mainEntity: { '@id': `${origin}/#organization` },
        }),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'About', url: canonicalUrl },
        ]),
    ];
};

/**
 * Terms, privacy and the refund policy share one builder. They carry no product
 * claim, so there is no `SoftwareApplication` node here: the point is to attach
 * the publisher identity and a breadcrumb, nothing more.
 */
export const buildLegalPageStructuredData = ({
    canonicalUrl,
    description,
    title,
}: {
    canonicalUrl: string;
    description: string;
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);

    return [
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: { '@id': `${origin}/#website` },
            publisher: { '@id': `${origin}/#organization` },
        }),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: title, url: canonicalUrl },
        ]),
    ];
};

/**
 * The subscribe page describes the weekly sends (one list item per scheduled email), so
 * the newsletter itself is the `mainEntity` rather than the product. `WebPage` keeps the
 * description on the page and the `Organization` node keeps the publisher identity stable.
 */
export const buildNewsletterStructuredData = ({
    canonicalUrl,
    description,
    sends,
    title,
}: {
    canonicalUrl: string;
    description: string;
    sends: ReadonlyArray<{ name: string; cadence: string }>;
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);

    return [
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: { '@id': `${origin}/#website` },
            mainEntity: { '@id': `${canonicalUrl}#itemlist` },
        }),
        jsonLdNode({
            '@type': 'ItemList',
            '@id': `${canonicalUrl}#itemlist`,
            isPartOf: { '@id': `${canonicalUrl}#webpage` },
            name: 'Unique Domains email sends',
            numberOfItems: sends.length,
            itemListElement: sends.map((send, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: `${send.name}, ${send.cadence}`,
            })),
        }),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'Newsletter', url: canonicalUrl },
        ]),
    ];
};

export const buildDomainingNewsArchiveStructuredData = ({
    canonicalUrl,
    description,
    editions,
    title,
}: {
    canonicalUrl: string;
    description: string;
    editions: readonly PublishedNewsEditionCard[];
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);

    return [
        jsonLdNode({
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: { '@id': `${origin}/#website` },
            mainEntity: { '@id': `${canonicalUrl}#itemlist` },
        }),
        jsonLdNode({
            '@type': 'ItemList',
            '@id': `${canonicalUrl}#itemlist`,
            isPartOf: { '@id': `${canonicalUrl}#webpage` },
            name: 'Domaining News daily editions',
            numberOfItems: editions.length,
            itemListElement: editions.map((edition, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: edition.headline,
                url: new URL(edition.path, origin).toString(),
            })),
        }),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'Domaining News', url: `${origin}/resources/domaining-news` },
        ]),
    ];
};

export const buildDomainingNewsEditionStructuredData = ({
    canonicalUrl,
    categoryLabels,
    edition,
    imageUrl,
}: {
    canonicalUrl: string;
    categoryLabels: readonly string[];
    edition: PublishedNewsEdition;
    imageUrl: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);
    const datesAndLaunches = edition.datesAndLaunches ?? [];
    const articleBody = [
        edition.lede?.text,
        ...edition.bodySections.map((section) => [
            `${section.heading}. ${section.text}`,
            ...section.rows.map((row) => `${row.label}: ${row.value}.`),
        ].join(' ')),
        ...edition.briefs.map((brief) => `${brief.title}. ${brief.text}`),
        ...edition.watchItems.map((watchItem) => [
            watchItem.dateLabel ? `${watchItem.dateLabel}. ` : '',
            `${watchItem.title} ${watchItem.text}`,
        ].join('')),
        edition.conclusion?.text,
        // The dated block the article shows after its closing section, as one paragraph.
        datesAndLaunches.length
            ? [
                `${DOMAINING_NEWS_SECTION_LABELS.dates}.`,
                ...datesAndLaunches.map((item) => `${item.dateLabel}: ${item.title} (${item.badge}).`),
            ].join(' ')
            : null,
    ]
        .filter((value): value is string => Boolean(value))
        .join('\n\n');

    return compactNodes([
        jsonLdNode({
            '@type': 'NewsArticle',
            '@id': `${canonicalUrl}#article`,
            headline: edition.headline,
            description: edition.seoDescription,
            abstract: edition.summary,
            ...(articleBody ? { articleBody } : {}),
            datePublished: edition.publishedAt,
            dateModified: edition.updatedAt,
            inLanguage: 'en',
            isAccessibleForFree: true,
            articleSection: [...categoryLabels],
            image: imageUrl,
            author: { '@id': `${origin}/#organization` },
            publisher: { '@id': `${origin}/#organization` },
            mainEntityOfPage: { '@id': `${canonicalUrl}#webpage` },
            // A citation the sales-source guard unlinked has an empty url and is left out.
            citation: edition.citations.map((citation) => citation.url).filter(Boolean),
            about: categoryLabels.map((label) => ({
                '@type': 'DefinedTerm',
                name: label,
            })),
        }),
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: edition.headline,
            description: edition.seoDescription,
            datePublished: edition.publishedAt,
            dateModified: edition.updatedAt,
            inLanguage: 'en',
            isPartOf: { '@id': `${origin}/#website` },
            primaryImageOfPage: imageUrl,
            mainEntity: { '@id': `${canonicalUrl}#article` },
        }),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'Domaining News', url: `${origin}/resources/domaining-news` },
            { name: edition.headline, url: canonicalUrl },
        ]),
        faqSchema(
            canonicalUrl,
            edition.questionsAnswers.map((item) => ({
                q: item.question,
                a: item.answer,
            })),
        ),
    ]);
};

/*----------------------------------
- AGENDA AND TLD APPLICATIONS
----------------------------------*/

/** `/agenda` marks up at most this many events: the page ships its data twice (markup and `window.ssr`). */
export const AGENDA_EVENT_JSON_LD_LIMIT = 50;

/**
 * An agenda entry that may carry `Event` markup: an industry event (a conference or a meetup a
 * person attends) with a date and a known country. TLD launch phases and registry changes are
 * not attendable, so Google's event guidelines exclude them. An event with no country is left
 * out rather than marked `VirtualLocation`: the wire has no online flag and no event URL, and
 * a null country also means "venue not announced yet", so either location would be invented.
 */
const isAgendaEventMarkupEntry = (entry: AgendaEntry): entry is AgendaEntry & { startsOn: string; countryCode: string } =>
    entry.kind === 'industry_event' && entry.startsOn !== null && entry.countryCode !== null;

const agendaEventSchema = (
    entry: AgendaEntry & { startsOn: string; countryCode: string },
    formatCountry: (code: string) => string,
): WebsiteJsonLdObject => {
    const countryName = formatCountry(entry.countryCode);
    return {
        '@type': 'Event',
        name: entry.title,
        startDate: entry.startsOn,
        ...(entry.endsOn ? { endDate: entry.endsOn } : {}),
        // The agenda lists scheduled events only: a cancelled or moved one leaves the source.
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: {
            '@type': 'Place',
            name: entry.city ? `${entry.city}, ${countryName}` : countryName,
            address: {
                '@type': 'PostalAddress',
                ...(entry.city ? { addressLocality: entry.city } : {}),
                addressCountry: entry.countryCode,
            },
        },
    };
};

export const buildAgendaStructuredData = ({
    canonicalUrl,
    description,
    entries,
    formatCountry,
    title,
}: {
    canonicalUrl: string;
    description: string;
    /** The board's month entries, in date order; only the first 50 attendable events are marked up. */
    entries: readonly AgendaEntry[];
    /**
     * An ISO code to its English name, for the `Place` name. Passed in by the agenda route rather
     * than imported here: this module sits in the chunk every landing page shares, and the
     * country table (`@uniquedomains/core/geo`) must ship with the agenda page only.
     */
    formatCountry: (code: string) => string;
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);
    const events = entries.filter(isAgendaEventMarkupEntry).slice(0, AGENDA_EVENT_JSON_LD_LIMIT);

    return [
        jsonLdNode({
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: { '@id': `${origin}/#website` },
        }),
        // One node per event, each on its own in the graph, as event rich results read them.
        ...events.map((entry) => jsonLdNode(agendaEventSchema(entry, formatCountry))),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'Agenda', url: canonicalUrl },
        ]),
    ];
};

export const buildTldApplicationsStructuredData = ({
    canonicalUrl,
    description,
    hubUrl,
    rows,
    title,
}: {
    canonicalUrl: string;
    description: string;
    /** The `/tld` hub, the breadcrumb's parent. */
    hubUrl: string;
    /** This page's rows only, in the page's neutral order (string, then application id). */
    rows: readonly PublicTldApplication[];
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);

    return compactNodes([
        jsonLdNode({
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: { '@id': `${origin}/#website` },
            ...(rows.length ? { mainEntity: { '@id': `${canonicalUrl}#itemlist` } } : {}),
        }),
        // Before Reveal Day the page has no row, and an empty list is not a list.
        rows.length
            ? jsonLdNode({
                  '@type': 'ItemList',
                  '@id': `${canonicalUrl}#itemlist`,
                  isPartOf: { '@id': `${canonicalUrl}#webpage` },
                  name: 'Applied-for strings in the 2026 new gTLD round',
                  numberOfItems: rows.length,
                  itemListElement: rows.map((row, index) => ({
                      '@type': 'ListItem',
                      position: index + 1,
                      name: `.${row.uLabel || row.string}`,
                  })),
              })
            : null,
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'TLDs', url: hubUrl },
            { name: 'New gTLD applications', url: canonicalUrl },
        ]),
    ]);
};

/*----------------------------------
- TLD PAGES
----------------------------------*/

/**
 * `/tld`: the hub as a `CollectionPage` over the published insight pages, in the public set's
 * neutral order (seed, then alphabetical): a list of TLD pages, never a ranking of names.
 */
export const buildTldHubStructuredData = ({
    canonicalUrl,
    description,
    entries,
    title,
}: {
    canonicalUrl: string;
    description: string;
    /** The published insight pages, in Product's order, with their absolute URL. */
    entries: ReadonlyArray<{ slug: string; url: string }>;
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);

    return compactNodes([
        jsonLdNode({
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: { '@id': `${origin}/#website` },
            ...(entries.length ? { mainEntity: { '@id': `${canonicalUrl}#itemlist` } } : {}),
        }),
        entries.length
            ? jsonLdNode({
                  '@type': 'ItemList',
                  '@id': `${canonicalUrl}#itemlist`,
                  isPartOf: { '@id': `${canonicalUrl}#webpage` },
                  name: 'TLD pages',
                  numberOfItems: entries.length,
                  itemListElement: entries.map((entry, index) => ({
                      '@type': 'ListItem',
                      position: index + 1,
                      name: formatTldLabel(entry.slug),
                      url: entry.url,
                  })),
              })
            : null,
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'TLDs', url: canonicalUrl },
        ]),
    ]);
};

/**
 * `/sales` (ADR-0067): a `CollectionPage` over a `Dataset` of aggregates (dated by the nightly
 * rollup, its Markdown mirror as the distribution), the whole FAQ, and the breadcrumb. No
 * `ItemList` of sales: the markup names the dataset, never a sale or a domain.
 */
export const buildSalesStructuredData = ({
    canonicalUrl,
    description,
    title,
    markdownUrl,
    dateModified,
    temporalCoverage,
    faqs,
}: {
    canonicalUrl: string;
    description: string;
    title: string;
    markdownUrl: string;
    /** ISO timestamp of the nightly rollup. */
    dateModified: string;
    /** `YYYY-MM-DD/YYYY-MM-DD`, the archive's first usable sale to the rollup day. */
    temporalCoverage: string | null;
    faqs: ReadonlyArray<{ question: string; answer: string }>;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);
    return compactNodes([
        jsonLdNode({
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            dateModified,
            isPartOf: { '@id': `${origin}/#website` },
            mainEntity: { '@id': `${canonicalUrl}#dataset` },
        }),
        jsonLdNode({
            '@type': 'Dataset',
            '@id': `${canonicalUrl}#dataset`,
            name: title,
            description,
            url: canonicalUrl,
            dateModified,
            ...(temporalCoverage ? { temporalCoverage } : {}),
            isAccessibleForFree: true,
            conditionsOfAccess: 'Aggregates are public. Single sales beyond the newest 20, their marketplace and earlier sales need Full Access.',
            creator: { '@id': `${origin}/#organization` },
            variableMeasured: ['Usable sales by window', 'Median sale price', 'Sales by ending', 'Sales by word length', 'Price band mix'],
            distribution: [{ '@type': 'DataDownload', encodingFormat: 'text/markdown', contentUrl: markdownUrl }],
        }),
        faqSchema(canonicalUrl, toStructuredDataFaqs(faqs), faqs.length),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'Domain sales', url: canonicalUrl },
        ]),
    ]);
};

/**
 * The auctions page (ADR-0076): a `CollectionPage` over a `Dataset` of the day's aggregates, its
 * FAQ and its breadcrumb. No `ItemList`: a sampled name never enters the JSON-LD (ADR-0028).
 */
export const buildAuctionsStructuredData = ({
    canonicalUrl,
    description,
    title,
    breadcrumb,
    markdownUrl,
    dateModified,
    day,
    faqs,
}: {
    canonicalUrl: string;
    description: string;
    title: string;
    /** The page's name in the breadcrumb, from its content catalog. */
    breadcrumb: string;
    markdownUrl: string;
    /** ISO timestamp of the scanner's latest read. */
    dateModified: string | null;
    /** The UTC day of the sample and the counts. */
    day: string;
    faqs: ReadonlyArray<{ question: string; answer: string }>;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);
    return compactNodes([
        jsonLdNode({
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            ...(dateModified ? { dateModified } : {}),
            isPartOf: { '@id': `${origin}/#website` },
            mainEntity: { '@id': `${canonicalUrl}#dataset` },
        }),
        jsonLdNode({
            '@type': 'Dataset',
            '@id': `${canonicalUrl}#dataset`,
            name: title,
            description,
            url: canonicalUrl,
            ...(dateModified ? { dateModified } : {}),
            temporalCoverage: day,
            isAccessibleForFree: true,
            conditionsOfAccess: `The day’s counts and the venue rules are public. Every closing name beyond the day’s sample of ${PUBLIC_AUCTIONS_ROW_LIMIT} needs Full Access.`,
            creator: { '@id': `${origin}/#organization` },
            variableMeasured: ['Auctions closing today by venue', 'Auctions closing today by ending', 'Auctions closing today by word length', 'Share with a bid', 'Median bid'],
            distribution: [{ '@type': 'DataDownload', encodingFormat: 'text/markdown', contentUrl: markdownUrl }],
        }),
        faqSchema(canonicalUrl, toStructuredDataFaqs(faqs), faqs.length),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: breadcrumb, url: canonicalUrl },
        ]),
    ]);
};

/**
 * The registrar hub (ADR-0079): a `CollectionPage` over a `Dataset` of the price lists, the
 * registrars in catalog order as an `ItemList` (names only, never a price beside a name), its FAQ
 * and its breadcrumb. The day's 40 ending rows never enter the JSON-LD.
 */
export const buildRegistrarsStructuredData = ({
    canonicalUrl,
    description,
    title,
    breadcrumb,
    markdownUrl,
    dateModified,
    registrars,
    faqs,
    week,
    conditionsOfAccess,
}: {
    canonicalUrl: string;
    description: string;
    title: string;
    /** The page's name in the breadcrumb, from its content catalog. */
    breadcrumb: string;
    markdownUrl: string;
    /** ISO timestamp of the latest price list read. */
    dateModified: string | null;
    /**
     * In catalog order, as the hub lists them, each with its clean homepage (`siteUrl`): never the
     * outbound `homepageUrl`, which carries our UTM parameters.
     */
    registrars: ReadonlyArray<{ name: string; siteUrl: string | null }>;
    faqs: ReadonlyArray<{ question: string; answer: string }>;
    /** The ISO week the change counts cover, to the rotation day. */
    week: { from: string; to: string };
    /** Who may read what, from the page's content catalog (the CSV, like every member control, is Full Access's). */
    conditionsOfAccess: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);
    return compactNodes([
        jsonLdNode({
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            ...(dateModified ? { dateModified } : {}),
            isPartOf: { '@id': `${origin}/#website` },
            mainEntity: { '@id': `${canonicalUrl}#dataset` },
        }),
        jsonLdNode({
            '@type': 'ItemList',
            '@id': `${canonicalUrl}#itemlist`,
            isPartOf: { '@id': `${canonicalUrl}#webpage` },
            name: 'Registrars compared',
            numberOfItems: registrars.length,
            itemListElement: registrars.map((row, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                item: { '@type': 'Organization', name: row.name, ...(row.siteUrl ? { url: row.siteUrl } : {}) },
            })),
        }),
        jsonLdNode({
            '@type': 'Dataset',
            '@id': `${canonicalUrl}#dataset`,
            name: title,
            description,
            url: canonicalUrl,
            ...(dateModified ? { dateModified } : {}),
            temporalCoverage: `${week.from}/${week.to}`,
            isAccessibleForFree: true,
            conditionsOfAccess,
            creator: { '@id': `${origin}/#organization` },
            // Only what the Markdown distribution carries (`buildRegistrarsMarkdown`): counts, never a
            // price, since a mirror never prints a price beside a name or the day's rows.
            variableMeasured: ['Endings priced by registrar', 'Endings where a registrar’s renewal is the lowest', 'Confirmed price changes this week, up and down', 'Renewal traps (count)', 'Names pointing to each marketplace (rounded)'],
            distribution: [{ '@type': 'DataDownload', encodingFormat: 'text/markdown', contentUrl: markdownUrl }],
        }),
        faqSchema(canonicalUrl, toStructuredDataFaqs(faqs), faqs.length),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: breadcrumb, url: canonicalUrl },
        ]),
    ]);
};

/**
 * A registrar or venue profile (ADR-0079): a `WebPage` about the company as an `Organization`
 * (its IANA id as `identifier` when accredited), a `Dataset` of its price list for a registrar
 * only (a venue publishes counts, not a price list), its FAQ and the breadcrumb under the hub.
 */
export const buildRegistrarProfileStructuredData = ({
    canonicalUrl,
    description,
    title,
    hubUrl,
    hubLabel,
    markdownUrl,
    organization,
    dataset,
    faqs,
    conditionsOfAccess,
}: {
    canonicalUrl: string;
    description: string;
    title: string;
    hubUrl: string;
    hubLabel: string;
    markdownUrl: string;
    /**
     * `siteUrl` is the company's clean homepage; the outbound `homepageUrl` carries our UTM
     * parameters (Product's `buildRegistrarHomepageUrl`) and never enters the JSON-LD.
     */
    organization: { name: string; ianaId: number | null; siteUrl: string | null };
    /** Null for a venue. */
    dataset: { dateModified: string | null; endingsPriced: number } | null;
    faqs: ReadonlyArray<{ question: string; answer: string }>;
    /** The Dataset's access line, from the page's content catalog; unused for a venue. */
    conditionsOfAccess: (endingsPriced: string) => string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);
    return compactNodes([
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            ...(dataset?.dateModified ? { dateModified: dataset.dateModified } : {}),
            isPartOf: { '@id': `${origin}/#website` },
            about: {
                '@type': 'Organization',
                name: organization.name,
                ...(organization.siteUrl ? { url: organization.siteUrl } : {}),
                ...(organization.ianaId !== null ? { identifier: { '@type': 'PropertyValue', propertyID: 'IANA Registrar ID', value: String(organization.ianaId) } } : {}),
            },
        }),
        dataset
            ? jsonLdNode({
                  '@type': 'Dataset',
                  '@id': `${canonicalUrl}#dataset`,
                  name: title,
                  description,
                  url: canonicalUrl,
                  ...(dataset.dateModified ? { dateModified: dataset.dateModified } : {}),
                  isAccessibleForFree: true,
                  conditionsOfAccess: conditionsOfAccess(formatTldCount(dataset.endingsPriced)),
                  creator: { '@id': `${origin}/#organization` },
                  // Only what the Markdown distribution carries (`buildRegistrarProfileMarkdown`): counts, never a price.
                  variableMeasured: ['Endings priced', 'Endings where its renewal is the lowest', 'Confirmed price changes this week, up and down'],
                  distribution: [{ '@type': 'DataDownload', encodingFormat: 'text/markdown', contentUrl: markdownUrl }],
              })
            : null,
        faqSchema(canonicalUrl, toStructuredDataFaqs(faqs), faqs.length),
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: hubLabel, url: hubUrl },
            { name: organization.name, url: canonicalUrl },
        ]),
    ]);
};

/** What both lookup hubs pass in: their own words and the hub's dated counts, never a looked-up name. */
type LookupToolStructuredDataInput = {
    canonicalUrl: string;
    description: string;
    title: string;
    /** The page's name in the breadcrumb and the application's name. */
    breadcrumb: string;
    markdownUrl: string;
    /** The hub's `asOf`: the daily counts move it. */
    dateModified: string;
    /** The page's own FAQ, the same entries it renders, through `toStructuredDataFaqs`. */
    faqs: WebsiteDomainsLandingSeoFaq[];
};

/**
 * A lookup hub (ADR-0078): a `WebPage` whose main entity is the free `WebApplication` (one live
 * lookup a day, a zero-price offer), the FAQ answered with the day's counts, and the breadcrumb.
 * Built from the hub only: a looked-up name never enters the markup, and the result pages
 * (`/whois/<name>`, `/availability/<query>`) emit none.
 */
const buildLookupToolStructuredData = (input: LookupToolStructuredDataInput, featureList: string[]): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(input.canonicalUrl);
    return compactNodes([
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${input.canonicalUrl}#webpage`,
            url: input.canonicalUrl,
            name: input.title,
            description: input.description,
            inLanguage: 'en',
            dateModified: input.dateModified,
            isPartOf: { '@id': `${origin}/#website` },
            mainEntity: { '@id': `${input.canonicalUrl}#app` },
            associatedMedia: { '@type': 'MediaObject', encodingFormat: 'text/markdown', contentUrl: input.markdownUrl },
        }),
        jsonLdNode({
            '@type': 'WebApplication',
            '@id': `${input.canonicalUrl}#app`,
            name: input.breadcrumb,
            url: input.canonicalUrl,
            description: input.description,
            applicationCategory: 'UtilitiesApplication',
            operatingSystem: 'Web',
            isAccessibleForFree: true,
            featureList,
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: 'One live lookup a day, no account needed.' },
            publisher: { '@id': `${origin}/#organization` },
        }),
        faqSchema(input.canonicalUrl, input.faqs, input.faqs.length),
        breadcrumbSchema(input.canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: input.breadcrumb, url: input.canonicalUrl },
        ]),
    ]);
};

export const buildWhoisStructuredData = (input: LookupToolStructuredDataInput): WebsiteJsonLdNode[] =>
    buildLookupToolStructuredData(input, ['Registrar and registry record', 'Status codes in plain words', 'Exact drop date in pending delete', 'Nameservers and DNS', 'RDAP and WHOIS']);

export const buildAvailabilityStructuredData = (input: LookupToolStructuredDataInput): WebsiteJsonLdNode[] =>
    buildLookupToolStructuredData(input, ['Registry availability answer', 'Price today and renewal', 'Five-year cost to hold', 'Same word on other endings', 'Rating band']);

/**
 * An editions page (ADR-0074): a `CollectionPage` dated by its edition, the edition's trends as an
 * `ItemList` of trend names only, the page's Q&A and its breadcrumb. No row: no name, no price and
 * no verdict in the markup (ADR-0028 amendment of 2026-10-04); the page and its mirror carry the
 * five open names.
 */
export const buildEditionsStructuredData = ({
    canonicalUrl,
    description,
    title,
    markdownUrl,
    datePublished,
    trends,
    faqs,
    breadcrumbs,
}: {
    canonicalUrl: string;
    description: string;
    title: string;
    markdownUrl: string;
    /** ISO timestamp of the edition's publication, null on an archive month. */
    datePublished: string | null;
    trends: ReadonlyArray<{ name: string; url: string }>;
    faqs: ReadonlyArray<{ question: string; answer: string }>;
    breadcrumbs: ReadonlyArray<{ name: string; url: string }>;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);
    return compactNodes([
        jsonLdNode({
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            ...(datePublished ? { datePublished, dateModified: datePublished } : {}),
            isPartOf: { '@id': `${origin}/#website` },
            publisher: { '@id': `${origin}/#organization` },
            encoding: { '@type': 'MediaObject', encodingFormat: 'text/markdown', contentUrl: markdownUrl },
            ...(trends.length ? { mainEntity: { '@id': `${canonicalUrl}#trends` } } : {}),
        }),
        trends.length
            ? jsonLdNode({
                '@type': 'ItemList',
                '@id': `${canonicalUrl}#trends`,
                name: `Trends in ${title}`,
                numberOfItems: trends.length,
                itemListElement: trends.map((trend, index) => ({ '@type': 'ListItem', position: index + 1, name: trend.name, url: trend.url })),
            })
            : null,
        faqs.length ? faqSchema(canonicalUrl, toStructuredDataFaqs(faqs), faqs.length) : null,
        breadcrumbSchema(canonicalUrl, [{ name: 'Home', url: `${origin}/` }, ...breadcrumbs]),
    ]);
};

/**
 * `/tld/<slug>`: a `WebPage` about the TLD (a `DefinedTerm` of the set the `/tld` hub lists), and
 * the bounded name sample as an `ItemList` of names only. No `Dataset` figures, no price and no
 * sale in the markup: the page shows those, the markup only names what the page is about.
 */
export const buildTldInsightStructuredData = ({
    canonicalUrl,
    description,
    hubUrl,
    insight,
    title,
}: {
    canonicalUrl: string;
    description: string;
    /** The `/tld` hub, the breadcrumb's parent. */
    hubUrl: string;
    insight: Pick<PublicTldInsight, 'slug' | 'sample'>;
    title: string;
}): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);
    const tld = formatTldLabel(insight.slug);
    const sample = insight.sample.slice(0, TLD_INSIGHT_SAMPLE_LIMIT);

    return compactNodes([
        jsonLdNode({
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: title,
            description,
            inLanguage: 'en',
            isPartOf: { '@id': `${origin}/#website` },
            // A URL, as schema.org allows for `inDefinedTermSet`: the hub is the list of TLD pages.
            about: { '@type': 'DefinedTerm', name: tld, inDefinedTermSet: hubUrl },
        }),
        sample.length
            ? jsonLdNode({
                  '@type': 'ItemList',
                  '@id': `${canonicalUrl}#itemlist`,
                  isPartOf: { '@id': `${canonicalUrl}#webpage` },
                  name: `One-word ${tld} names, sample`,
                  numberOfItems: sample.length,
                  itemListElement: sample.map((row, index) => ({
                      '@type': 'ListItem',
                      position: index + 1,
                      name: row.domain,
                  })),
              })
            : null,
        breadcrumbSchema(canonicalUrl, [
            { name: 'Home', url: `${origin}/` },
            { name: 'TLDs', url: hubUrl },
            { name: tld, url: canonicalUrl },
        ]),
    ]);
};

export const buildDomainsStructuredData = ({
    canonicalUrl,
    currentPath,
    description,
    faqs,
    geoContext,
    previewRows,
    title,
}: DomainsStructuredDataContext): WebsiteJsonLdNode[] => {
    const origin = originFromUrl(canonicalUrl);
    const kind = resolveDomainsStructuredDataKind(canonicalUrl, currentPath);
    const current = toUrl(currentPath || new URL(canonicalUrl).pathname, origin);
    const normalizedPath = current.pathname.replace(/\/+$/, '') || '/domains';
    const sectorSlug = normalizedPath.match(/^\/domains\/sector\/([^/]+)$/)?.[1];
    const itemList = itemListSchema({ canonicalUrl, name: title, previewRows });
    const baseBreadcrumb = [
        { name: 'Home', url: `${origin}/` },
        { name: 'Domains', url: `${origin}/domains` },
    ];

    if (kind === 'domains_index') {
        return compactNodes([
            jsonLdNode({
                '@type': 'CollectionPage',
                '@id': `${canonicalUrl}#webpage`,
                url: canonicalUrl,
                name: geoContext?.title || 'One-word domain database',
                description:
                    geoContext?.shortAnswer ||
                    'Public entry point to the Unique Domains Screener for one-word domain discovery, filtering, and decision support.',
                isPartOf: { '@id': `${origin}/#website` },
                about: { '@id': `${origin}/domains#dataset` },
                mainEntity: { '@id': `${origin}/domains#dataset` },
            }),
            jsonLdNode({
                '@type': 'Dataset',
                '@id': `${origin}/domains#dataset`,
                name: 'Unique Domains one-word domain database',
                description:
                    'A one-word domain database with public preview and product workflows for search, Radars, and Watchlists.',
                creator: { '@id': `${origin}/#organization` },
                keywords: [
                    'one-word domains',
                    'domain intelligence',
                    'domain investing',
                    'startup domains',
                    'domain monitoring',
                    'domain Screener',
                ],
                // The page a crawler indexes is genuinely free: /domains serves the
                // sanitized preview to anonymous visitors. Declaring it paywalled
                // asked Google for `hasPart` selectors that do not exist, and the
                // plan-dependent depth is what `conditionsOfAccess` is for.
                isAccessibleForFree: true,
                conditionsOfAccess:
                    "The public preview at /domains is free and needs no account. Deeper search, filters, and analysis depend on the member's plan.",
            }),
            breadcrumbSchema(canonicalUrl, baseBreadcrumb),
            faqSchema(canonicalUrl, faqs),
        ]);
    }

    if (kind === 'domains_search') {
        return compactNodes([
            jsonLdNode({
                '@type': 'SearchResultsPage',
                '@id': `${canonicalUrl}#webpage`,
                url: canonicalUrl,
                name: geoContext?.title || title || 'Unique Domains search results',
                description: geoContext?.shortAnswer || description,
                isPartOf: { '@id': `${origin}/#website` },
                mainEntity: { '@id': `${canonicalUrl}#itemlist` },
            }),
            itemList,
            breadcrumbSchema(canonicalUrl, [
                ...baseBreadcrumb,
                { name: title || 'Search results', url: canonicalUrl },
            ]),
            faqSchema(canonicalUrl, faqs),
        ]);
    }

    if (kind === 'sector' && sectorSlug) {
        const sector = sectorLabelFromSlug(sectorSlug);
        return compactNodes([
            jsonLdNode({
                '@type': 'CollectionPage',
                '@id': `${canonicalUrl}#webpage`,
                url: canonicalUrl,
                name: geoContext?.title || `One-word domains for ${sector}`,
                description:
                    geoContext?.shortAnswer ||
                    `Public entry point for one-word domains that may fit ${sector} products, investors, founders, or client briefs.`,
                isPartOf: { '@id': `${origin}/#website` },
                mainEntity: { '@id': `${canonicalUrl}#collection` },
            }),
            jsonLdNode({
                '@type': 'Collection',
                '@id': `${canonicalUrl}#collection`,
                name: geoContext?.title || `One-word ${sector} domains`,
                description: geoContext?.fit
                    ? `${geoContext.fit} Evaluated through fit, ownership, and risk.`
                    : `A collection page for ${sector}-relevant one-word domains, evaluated through fit, ownership, and risk.`,
                about: { '@type': 'DefinedTerm', name: `${sector} domains` },
                mainEntityOfPage: { '@id': `${canonicalUrl}#webpage` },
            }),
            itemList,
            breadcrumbSchema(canonicalUrl, [
                ...baseBreadcrumb,
                { name: `${sector} domains`, url: canonicalUrl },
            ]),
            faqSchema(canonicalUrl, faqs),
        ]);
    }

    return [];
};
